// Builds a Shopify cart permalink (hosted checkout) from local cart lines.
// The shop API is read-only (no cart/checkout endpoint), so checkout hands off
// to Shopify's own /cart/{variantId}:{qty},... permalink. Returns null when no
// storefront domain is configured (VITE_SHOPIFY_DOMAIN) or the cart is empty,
// so the UI can disable the button rather than link nowhere.

const DEFAULT_STORE_DOMAIN = 'helinhair-2.myshopify.com'

/**
 * Storefront domain used to build the cart permalink. Overridable via env.
 * The fallback is for local-dev convenience; deployed builds should set the
 * env var explicitly so stg can't silently send shoppers to the prod
 * Shopify checkout. We warn whenever the fallback fires.
 */
export function shopifyStoreDomain(): string {
  const raw = (import.meta.env.VITE_SHOPIFY_DOMAIN as string | undefined)?.trim()
  if (raw && raw.length > 0) return raw
  console.warn(
    '[shop] VITE_SHOPIFY_DOMAIN not set — using prod store as fallback. ' +
      'Set it explicitly in your env to avoid silent prod-checkout in stg.',
  )
  return DEFAULT_STORE_DOMAIN
}

export interface CheckoutLine {
  variantId: string
  quantity: number
}

/**
 * Builds `https://{domain}/cart/{variantId}:{qty},{variantId}:{qty}` from the
 * cart lines — Shopify's hosted-checkout permalink. Returns null when there are
 * no usable lines or no domain.
 */
export function shopifyCartUrl(items: CheckoutLine[], domain: string | undefined): string | null {
  const host = domain?.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '')
  if (!host) return null
  const line = items
    .filter((i) => i.variantId && i.quantity > 0)
    .map((i) => `${i.variantId}:${i.quantity}`)
    .join(',')
  if (!line) return null
  return `https://${host}/cart/${line}`
}
